import React, { useEffect, useRef, useState } from 'react';
import { questionAPI } from '../../api';
import './Exam.css';

interface Props {
  onSelect: (subject: any, examType: '필기' | '실기', mode: 'normal' | 'mock' | 'wrong') => void;
  initialSubjectName?: string;
}

const SubjectSelect: React.FC<Props> = ({ onSelect, initialSubjectName }) => {
  const [examType, setExamType] = useState<'필기' | '실기'>('필기');
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState<any>(null);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setSelectedSubject(null);
    setSearch('');
    setOpenCategory(null);
    questionAPI.subjects(examType).then(res => {
      const data = res.data;
      setSubjects(data);
      setLoading(false);
      if (initialSubjectName) {
        const found = data.find((s: any) =>
          s.name === initialSubjectName || s.name.includes(initialSubjectName)
        );
        if (found) setSelectedSubject(found);
      }
    });
  }, [examType, initialSubjectName]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = search.trim()
    ? subjects.filter(s => s.name.includes(search.trim()))
    : subjects;

  // 주요 과목 (카테고리 없음)
  const mainSubjects = filtered.filter(s => !s.category);

  // 카테고리별 그룹
  const categoryMap: Record<string, any[]> = {};
  filtered.filter(s => s.category).forEach(s => {
    if (!categoryMap[s.category]) categoryMap[s.category] = [];
    categoryMap[s.category].push(s);
  });
  const categories = Object.keys(categoryMap);

  const handleSubjectClick = (s: any) => {
    if (!s.is_active) return;
    setSelectedSubject((prev: any) => prev?.id === s.id ? null : s);
    setOpenCategory(null);
  };

  const handleSubjectDblClick = (s: any) => {
    if (!s.is_active) return;
    onSelect(s, examType, 'normal');
  };

  return (
    <div className="subject-select">
      {/* 헤더 */}
      <div className="subject-header">
        <div className="subject-header-left">
          <h2 className="subject-title">📚 국가기사시험</h2>
          <div className="exam-type-tabs">
            {(['필기', '실기'] as const).map(type => (
              <button key={type}
                className={`exam-type-tab ${examType === type ? 'active' : ''}`}
                onClick={() => setExamType(type)}>
                {type === '필기' ? '📝' : '🔧'} {type}
              </button>
            ))}
          </div>
        </div>
        <div className="subject-search">
          <input className="form-control" placeholder="🔍 종목 검색..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: 200 }} />
          {search && (
            <button className="btn btn-ghost btn-sm" onClick={() => setSearch('')}>✕</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner" /></div>
      ) : (
        <div className="subject-content">

          {/* 주요 과목 */}
          {mainSubjects.length > 0 && (
            <div className="subject-group">
              <div className="subject-group-label">
                <span>⭐ 주요 과목</span>
                <span className="subject-group-count">{mainSubjects.length}개</span>
              </div>
              {/* ✅ 원본처럼 7열 그리드 */}
              <div className="subject-grid-7col">
                {mainSubjects.map(s => (
                  <button key={s.id}
                    className={`subject-card ${!s.is_active ? 'disabled' : ''} ${selectedSubject?.id === s.id ? 'selected' : ''}`}
                    onClick={() => handleSubjectClick(s)}
                    onDoubleClick={() => handleSubjectDblClick(s)}
                    title={s.is_active ? '더블클릭하면 바로 문제풀기' : '비활성 종목'}>
                    {!s.is_active && '🔒 '}{s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 카테고리별 그룹 - 원본처럼 7열 그리드 + 드롭다운 */}
          {categories.length > 0 && (
            <div className="subject-group" style={{ marginTop: 16 }}>
              <div className="subject-group-label">
                <span>📁 기타 과목 (카테고리별)</span>
                <span className="subject-group-count">{categories.length}개</span>
              </div>
              <div className="subject-grid-7col" ref={dropdownRef}>
                {categories.map(cat => (
                  <div key={cat} style={{ position: 'relative' }}>
                    {/* 카테고리 버튼 */}
                    <button
                      className="subject-card category-card"
                      onClick={() => setOpenCategory(openCategory === cat ? null : cat)}
                      style={{ width: '100%' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📂 {cat}
                      </span>
                      <span style={{ fontSize: 10, marginLeft: 4, flexShrink: 0 }}>
                        {openCategory === cat ? '▲' : '▼'}
                      </span>
                    </button>

                    {/* ✅ 드롭다운 - 원본과 동일한 구조 */}
                    {openCategory === cat && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, zIndex: 200,
                        background: 'white', border: '2px solid #3498db',
                        borderTop: 'none', borderRadius: '0 0 8px 8px',
                        maxHeight: 300, overflowY: 'auto',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        minWidth: 160,
                      }}>
                        {categoryMap[cat].map(s => (
                          <div key={s.id}
                            onClick={() => handleSubjectClick(s)}
                            onDoubleClick={() => handleSubjectDblClick(s)}
                            style={{
                              padding: '10px 12px',
                              background: selectedSubject?.id === s.id ? '#3498db' : s.is_active ? '#f8f9fa' : '#ecf0f1',
                              color: selectedSubject?.id === s.id ? 'white' : s.is_active ? '' : '#95a5a6',
                              border: '1px solid #ddd', borderRadius: 4,
                              cursor: s.is_active ? 'pointer' : 'not-allowed',
                              fontSize: 13, marginBottom: 4, whiteSpace: 'nowrap',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => {
                              if (s.is_active && selectedSubject?.id !== s.id) {
                                (e.currentTarget as HTMLElement).style.background = '#3498db';
                                (e.currentTarget as HTMLElement).style.color = 'white';
                              }
                            }}
                            onMouseLeave={e => {
                              if (s.is_active && selectedSubject?.id !== s.id) {
                                (e.currentTarget as HTMLElement).style.background = '#f8f9fa';
                                (e.currentTarget as HTMLElement).style.color = '';
                              }
                            }}>
                            {!s.is_active && '🔒 '}{s.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 검색 결과 없음 */}
          {filtered.length === 0 && (
            <div className="empty-state" style={{ padding: 60 }}>
              <div className="icon">🔍</div>
              <p>"{search}"에 해당하는 종목이 없습니다.</p>
            </div>
          )}

          {/* 종목 없음 */}
          {!search && subjects.length === 0 && (
            <div className="empty-state" style={{ padding: 60 }}>
              <div className="icon">📭</div>
              <p>등록된 종목이 없습니다.</p>
            </div>
          )}
        </div>
      )}

      {/* 선택된 종목 + 모드 선택 바 */}
      {selectedSubject && (
        <div className="mode-select-bar">
          <div className="mode-select-subject">
            <span className="mode-subject-badge">{examType}</span>
            <strong>{selectedSubject.name}</strong>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>선택됨</span>
          </div>
          <div className="mode-btns">
            <button className="btn btn-primary" onClick={() => onSelect(selectedSubject, examType, 'normal')}>
              📝 일반 풀기
            </button>
            <button className="btn btn-success" onClick={() => onSelect(selectedSubject, examType, 'mock')}>
              📊 모의고사
            </button>
            <button className="btn btn-warning" onClick={() => onSelect(selectedSubject, examType, 'wrong')}>
              ❌ 오답노트
            </button>
            <button className="btn btn-ghost" onClick={() => setSelectedSubject(null)}>
              ✕ 취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectSelect;
